import type {
	AnyService,
	DependencyOverridesOf,
	ResolvedServiceDependencies,
	ServiceDependenciesBase,
	ToResolvedService,
} from './types';
import { isServiceWithDeps } from './utils';

/**
 * Returns a resolved service, i.e. a wrapper function that no longer
 * needs the dependency context param (the param is bound using dependency injection).
 *
 * **Details:**
 * If `service` is a function with `.dependencies`, resolving
 * returns a wrapping function that no longer needs the second param (the dependency context).
 * That param will be bound, and automatically injected, using services in `providedDeps`
 * to fill in any abstract dependencies, or even override non-abstract dependencies.
 * Runtime dependency resolution is lazy: delayed until the service calls `context.dependencies()`.
 *
 * If `service` doesn't have dependencies, it's returned verbatim.
 *
 * @param service The service to be resolved.
 * @param providedDeps Dependencies to inject into `service`. These are NOT resolved.
 * @returns The `service`, with its dependencies injected (bound wrapper function).
 */
export function resolveService<DeclaredService extends AnyService>(
	service: DeclaredService,
	providedDeps: DependencyOverridesOf<DeclaredService>,
): ToResolvedService<DeclaredService> {
	if (typeof service === 'function' && isServiceWithDeps(service)) {
		return ((input: Parameters<typeof service>[0]) => {
			return service(
				input,
				ResolvedServiceDependencyContext(service.dependencies, providedDeps ?? {}),
			);
		}) as ToResolvedService<DeclaredService>;
	}
	return service as ToResolvedService<DeclaredService>;
}

const ResolvedServiceDependencyContext = <Deps extends ServiceDependenciesBase>(
	declaredDependencies: Deps,
	depsOverrides: Partial<ResolvedServiceDependencies<Deps>>,
) => {
	let resolvedDeps = undefined as ResolvedServiceDependencies<Deps> | undefined;
	return {
		dependencies() {
			if (resolvedDeps) {
				return resolvedDeps;
			}
			resolvedDeps = {} as ResolvedServiceDependencies<Deps>;
			for (let [k, dependency] of Object.entries(declaredDependencies)) {
				const key = k as keyof Deps;
				resolvedDeps[key] =
					// TODO is this type-safe?
					// TODO resolve the override dep as well?
					(
						key in depsOverrides ? depsOverrides[key] : resolveService(dependency, depsOverrides)
					) as any;
			}
			return resolvedDeps;
		},
	};
};

// TODO: support multiple services to be resolved with common deps
// export function resolveServices<DeclaredServices extends DeclaredServicesBase>(
// 	services: DeclaredServices,
// 	deps: any /*TODO*/,
// ) {
// 	return services as ResolvedServices<DeclaredServices>;
// }
